import Database from '../types/Database'

/**
 * Counts the number of true ticks in a RatingTicks object.
 * Returns a value between 0 and 5 inclusive.
 */
export const calculateTickCount = (ticks: Database.RatingTicks): number => {
  return [
    ticks.showedUp,
    ticks.divedResponsibly,
    ticks.knewGearSetup,
    ticks.goodBuddy,
    ticks.wouldDiveAgain
  ].filter(Boolean).length
}

/**
 * Recalculates rating aggregates given the current aggregates and a new tick count.
 * Increments totalRatingsReceived and computes a new rolling averageTickScore.
 * perfectGuidedDiveRatings is left unchanged (handled separately).
 */
export const calculateUpdatedAggregates = (
  current: Database.RatingAggregates,
  newTickCount: number
): Database.RatingAggregates => {
  const newTotal = current.totalRatingsReceived + 1
  const newAverage =
    (current.averageTickScore * current.totalRatingsReceived + newTickCount) /
    newTotal

  return {
    totalRatingsReceived: newTotal,
    averageTickScore: newAverage,
    perfectGuidedDiveRatings: current.perfectGuidedDiveRatings
  }
}

/**
 * Determines if a rating qualifies as a "perfect guided dive rating".
 * Returns true only when all 5 ticks are true, the dive type is "guided",
 * and the ratee is NOT the host of the dive.
 */
export const isPerfectGuidedRating = (
  ticks: Database.RatingTicks,
  diveType: string,
  isRateeHost: boolean
): boolean => {
  if (diveType !== 'guided') return false
  if (isRateeHost) return false

  return (
    ticks.showedUp &&
    ticks.divedResponsibly &&
    ticks.knewGearSetup &&
    ticks.goodBuddy &&
    ticks.wouldDiveAgain
  )
}

/**
 * Creates initial rating aggregates for a user receiving their first rating.
 */
export const calculateInitialAggregates = (
  tickCount: number
): Database.RatingAggregates => {
  return {
    totalRatingsReceived: 1,
    averageTickScore: tickCount,
    perfectGuidedDiveRatings: 0
  }
}
